import cookieStore from '../services/cookie';
import { emitMobAppEvent } from '../services/web-view-service';
import { captureException } from './sentry';
import { getCspNonce } from '../utils/cspNonce';

declare const NSURE_APP_ID: string;
declare const NSURE_SCRIPT_URL: string;

interface NSureSharedState {
  deviceId?: string;
  promise: Promise<string>;
}

declare global {
  interface Window {
    nSureSDK?: {
      init: (appId: string) => void;
      getDeviceId: () => string | undefined;
    };
    nSureAsyncInit?: (deviceId: string) => void;
    paybisNSureState?: NSureSharedState;
  }
}

const NSURE_DEVICE_ID_KEY = 'nsure-device-id';
const COOKIE_EXPIRATION = new Date().setFullYear(new Date().getFullYear() + 1); // expires in 1 year

const LOAD_TIMEOUT_MS = 15_000;
const DEVICE_ID_TICK_INTERVAL_MS = 250;
const DEVICE_ID_MAX_TICKS = 60;

type NSureSDK = NonNullable<Window['nSureSDK']>;

function addDeviceId(deviceId: string) {
  document
    .querySelectorAll<HTMLInputElement>(`input[name="${ NSURE_DEVICE_ID_KEY }"]`)
    .forEach(element => {
      element.value = deviceId || '';
    });
}

function storeDeviceId(deviceId: string) {
  cookieStore.set(NSURE_DEVICE_ID_KEY, deviceId, {
    expires: new Date(COOKIE_EXPIRATION),
  });
}

function createScript() {
  const scriptElement = document.createElement('script');

  scriptElement.src = NSURE_SCRIPT_URL;
  scriptElement.type = 'text/javascript';
  scriptElement.defer = true;
  scriptElement.crossOrigin = 'anonymous';
  const nonce = getCspNonce();
  if (nonce) {
    scriptElement.nonce = nonce;
  }

  return scriptElement;
}

function appendScript(script: HTMLScriptElement) {
  if (document.head) {
    document.head.appendChild(script);
  } else {
    document.body.appendChild(script);
  }
}

function getNSureSDK(): NSureSDK {
  const sdk = window.nSureSDK;

  if (!sdk) {
    throw new Error('nSure SDK not available');
  }
  if (typeof sdk.init !== 'function') {
    throw new Error('nSure SDK init method not available');
  }
  if (typeof sdk.getDeviceId !== 'function') {
    throw new Error('nSure SDK getDeviceId method not available');
  }

  return sdk;
}

function toError(error: unknown): Error {
  return error instanceof Error ? error : new Error(String(error));
}

const fallbackDeviceId = cookieStore.get(NSURE_DEVICE_ID_KEY) || '';

function updateDeviceId(deviceId: string) {
  nSureState.deviceId = deviceId;

  if (typeof window === 'undefined') {
    return;
  }

  storeDeviceId(deviceId);
  addDeviceId(deviceId);
  emitMobAppEvent('nsure-device-id', { deviceId });
}

function reportError(error: unknown) {
  captureException({
    error: toError(error),
    extra: { deviceId: nSureState.deviceId ?? fallbackDeviceId },
  });
}

const createNSurePromise = () => {
  let loadTimeout = -1;
  let deviceIdTicker = -1;

  const clearTimers = () => {
    window.clearTimeout(loadTimeout);
    window.clearInterval(deviceIdTicker);
  };

  return new Promise<string>((resolve, reject) => {
    let settled = false;

    const resolveWithCleanup = (deviceId: string) => {
      if (settled) {
        return;
      }

      settled = true;
      clearTimers();
      resolve(deviceId);
    };

    const rejectWithCleanup = (error: unknown) => {
      if (settled) {
        return;
      }

      settled = true;
      clearTimers();
      reject(toError(error));
    };

    const handleInitializationError = (error: unknown) => {
      if (settled) {
        reportError(error);
        return;
      }

      rejectWithCleanup(error);
    };

    window.nSureAsyncInit = deviceId => {
      try {
        getNSureSDK().init(NSURE_APP_ID);

        if (!deviceId) {
          handleInitializationError(new Error('No deviceId found from nSure'));
          return;
        }

        if (settled) {
          updateDeviceId(deviceId);
          return;
        }

        resolveWithCleanup(deviceId);
      } catch (error: unknown) {
        handleInitializationError(error);
      }
    };

    const script = createScript();

    script.addEventListener('error', () => {
      rejectWithCleanup(new Error('nSure script failed to load'));
    }, { once: true });

    script.addEventListener('load', () => {
      if (settled) {
        return;
      }

      window.clearTimeout(loadTimeout);

      let sdk: NSureSDK;
      let deviceId: string | undefined;

      try {
        sdk = getNSureSDK();
        deviceId = sdk.getDeviceId();
      } catch (error: unknown) {
        rejectWithCleanup(error);
        return;
      }

      if (deviceId) {
        resolveWithCleanup(deviceId);
        return;
      }

      let ticks = 0;

      deviceIdTicker = window.setInterval(() => {
        ticks += 1;

        let polledDeviceId: string | undefined;

        try {
          polledDeviceId = getNSureSDK().getDeviceId();
        } catch (error: unknown) {
          rejectWithCleanup(error);
          return;
        }

        if (polledDeviceId) {
          resolveWithCleanup(polledDeviceId);
        } else if (ticks >= DEVICE_ID_MAX_TICKS) {
          rejectWithCleanup(new Error('No deviceId found from nSure'));
        }
      }, DEVICE_ID_TICK_INTERVAL_MS);
    }, { once: true });

    appendScript(script);

    loadTimeout = window.setTimeout(
      () => rejectWithCleanup(new Error('nSure script load timed out')),
      LOAD_TIMEOUT_MS,
    );
  }).catch((error: Error) => {
    reportError(error);

    return fallbackDeviceId;
  }).then(deviceId => {
    if (nSureState.deviceId !== undefined) {
      return nSureState.deviceId;
    }

    updateDeviceId(deviceId);

    return deviceId;
  }).finally(() => {
    clearTimers();
  });
};

function getNSureState(): NSureSharedState {
  if (typeof window === 'undefined') {
    return { promise: Promise.resolve(fallbackDeviceId) };
  }
  if (window.paybisNSureState) {
    return window.paybisNSureState;
  }

  const state = { promise: createNSurePromise() };
  window.paybisNSureState = state;

  return state;
}

const nSureState = getNSureState();

export const getDeviceId = (): Promise<string> => nSureState.deviceId !== undefined
  ? Promise.resolve(nSureState.deviceId)
  : nSureState.promise;
